import type { KyInstance } from 'ky';

import { DEFAULT_DATE_FORMAT, ensureError, formatDate, ifDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { CalendarData } from './types/calendar-data.type';
import type { GetCalendarDataQuery } from './types/get-calendar-data-query.type';

/**
 * Fetches calendar data from the API using the provided Ky instance and query parameters.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param query - The query parameters for fetching calendar data, including optional
 * start and end dates, and search string.
 * @param isExternal - Whether to use the external mailings endpoint. Defaults to `false`.
 * @returns A promise resolving to an operation result containing an array of calendar data.
 * @throws Throws an error if the request fails.
 */
export const getCalendarData = async (instance: KyInstance, query: GetCalendarDataQuery, isExternal = false) => {
  const { endDate, search, startDate } = query;

  try {
    return await instance
      .get<OperationResultWithData<CalendarData[]>>(isExternal ? Uri.calendar.getDataExternal : Uri.calendar.getData, {
        searchParams: {
          endDate: ifDefined(endDate, (date) => {
            return formatDate(date, DEFAULT_DATE_FORMAT);
          }),
          search,
          startDate: ifDefined(startDate, (date) => {
            return formatDate(date, DEFAULT_DATE_FORMAT);
          }),
        },
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
